import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Sparkles, X, Wand2, AlertTriangle, CheckCircle2, Loader2,
  ChevronDown, Calendar, Globe, Layers, BarChart3, Clock,
  FileText, Lightbulb, RefreshCw, ArrowRight, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const AISchedulePanel = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  projectStartDate,
  existingTaskCount = 0,
  onScheduleGenerated
}) => {
  // State
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedSchedule, setGeneratedSchedule] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [insertMode, setInsertMode] = useState('replace');
  // Pre-generation replace/append prompt when a macro schedule already exists
  const [showExistingSchedulePrompt, setShowExistingSchedulePrompt] = useState(false);
  const [chosenReplaceMode, setChosenReplaceMode] = useState('replace');
  
  // Async generation state
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const pollingRef = useRef(null);
  
  // Advanced options
  const [projectType, setProjectType] = useState('Infrastructure');
  const [projectSize, setProjectSize] = useState('Medium');
  const [planningLevel, setPlanningLevel] = useState('Detailed');
  const [country, setCountry] = useState('International');
  const [startDate, setStartDate] = useState(projectStartDate || new Date().toISOString().split('T')[0]);
  // Custom target task count (1..50000). null → use projectSize preset.
  const [customTarget, setCustomTarget] = useState('');
  // Soft-warning dialog when the requested target is huge (> 5,000)
  const [showBigWarning, setShowBigWarning] = useState(false);

  // Cleanup polling on unmount or close
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  // Poll for job status
  const pollJobStatus = useCallback(async (currentJobId) => {
    try {
      const response = await api.get(`/ai-schedule/status/${currentJobId}`);
      const { status, message, result } = response.data;
      
      setJobStatus(message);
      
      // Update progress based on status
      if (status === 'pending') {
        setProgress(10);
      } else if (status === 'processing') {
        setProgress(prev => Math.min(prev + 5, 85));
      } else if (status === 'completed') {
        setProgress(100);
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        
        if (result?.success) {
          setGeneratedSchedule(result);
          toast.success(`Generated ${result.task_count} tasks!`);
        } else {
          toast.error('Generation completed but no tasks returned');
        }
        setIsGenerating(false);
        setJobId(null);
      } else if (status === 'failed') {
        setProgress(0);
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        toast.error(message || 'AI generation failed');
        setIsGenerating(false);
        setJobId(null);
      }
    } catch (error) {
      console.error('Polling error:', error);
      // Don't stop polling on transient errors, just log
      if (error.response?.status === 404) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        toast.error('Job not found - please try again');
        setIsGenerating(false);
        setJobId(null);
      }
    }
  }, []);

  // Example prompts
  const examplePrompts = [
    {
      title: "Solar Power Plant (Large)",
      prompt: "Create an ultra-detailed EPC schedule for construction of a 200 MW solar power plant. Include: FEED, detailed engineering (civil, structural, electrical, mechanical), procurement (modules, inverters, transformers, cables, structures), civil works (site preparation, foundations, cable trenches), mechanical installation (module mounting, tracker systems), electrical installation (DC/AC wiring, substations, grid connection), testing and commissioning (string testing, inverter commissioning, grid synchronization), and project closeout. Use realistic durations based on industry standards."
    },
    {
      title: "Commercial Building (Large)", 
      prompt: "Generate a comprehensive construction schedule for a 20-story commercial office building. Include: pre-construction (permits, site surveys, design finalization), foundation work (excavation, piling, mat foundation), structural works (concrete core, steel frame, floor slabs), building envelope (curtain wall, roofing, waterproofing), MEP systems (HVAC, plumbing, electrical, fire protection), interior fit-out (partitions, ceilings, flooring, painting), vertical transportation (elevators, escalators), exterior works (landscaping, parking), and commissioning. Include all inspections and approvals."
    },
    {
      title: "Software Platform (Medium)",
      prompt: "Create a detailed software development schedule for an enterprise SaaS platform. Include: discovery phase (requirements, user research, competitive analysis), design phase (UX/UI design, prototyping, design system), backend development (architecture, database, APIs, authentication, integrations), frontend development (components, pages, responsive design), quality assurance (unit testing, integration testing, UAT, performance testing), deployment (staging, production, monitoring), and launch activities (documentation, training, support setup)."
    },
    {
      title: "Manufacturing Facility (Mega)",
      prompt: "Generate an ultra-detailed schedule for setting up a new automotive manufacturing facility. Include: site preparation (land development, utilities, roads), building construction (main plant, warehouses, offices, utilities buildings), equipment procurement (production lines, robotics, tooling, conveyors), installation (mechanical, electrical, pneumatic, controls), process development (production planning, quality systems, safety systems), workforce (hiring, training, certification), commissioning (equipment testing, line balancing, trial production), and ramp-up to full production."
    }
  ];

  // Effective target task count. Bounds: 1..50000
  const parsedTarget = (() => {
    const n = parseInt(customTarget, 10);
    if (!Number.isFinite(n)) return null;
    return Math.min(50000, Math.max(1, n));
  })();

  const runGenerate = useCallback(async () => {
    // Clear previous state
    setIsGenerating(true);
    setGeneratedSchedule(null);
    setJobStatus('Starting AI generation…');
    setProgress(5);

    try {
      // Start async generation job
      const response = await api.post('/ai-schedule/generate-async', {
        prompt,
        project_id: projectId,
        project_type: projectType,
        project_size: projectSize,
        planning_level: planningLevel,
        country,
        start_date: startDate,
        target_task_count: parsedTarget || undefined,
      });

      const { job_id, message } = response.data;
      setJobId(job_id);
      setJobStatus(message || 'Job started…');
      setProgress(10);

      if (parsedTarget && parsedTarget > 5000) {
        toast.info(`Generating ${parsedTarget.toLocaleString()} activities — this runs in the background with no client timeout.`);
      } else {
        toast.info('AI generation started — this may take 1-2 minutes for detailed schedules');
      }

      // Poll every 2s indefinitely. No client timeout.
      pollingRef.current = setInterval(() => { pollJobStatus(job_id); }, 2000);
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start schedule generation');
      setIsGenerating(false);
      setJobStatus(null);
      setProgress(0);
    }
  }, [prompt, projectId, projectType, projectSize, planningLevel, country, startDate, parsedTarget, pollJobStatus]);

  // Generate schedule using AI (async with polling)
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a schedule description');
      return;
    }
    // If a macro schedule already exists, prompt the user to choose replace/append/cancel BEFORE running the LLM
    if (existingTaskCount > 0) {
      setShowExistingSchedulePrompt(true);
      return;
    }
    // Soft-warn on huge targets
    if (parsedTarget && parsedTarget > 5000) {
      setShowBigWarning(true);
      return;
    }
    runGenerate();
  }, [prompt, parsedTarget, runGenerate, existingTaskCount]);

  // After user confirms replace/append on the pre-generation prompt
  const proceedAfterExistingPrompt = useCallback(() => {
    setInsertMode(chosenReplaceMode); // preselect the insert mode for post-gen confirm dialog
    setShowExistingSchedulePrompt(false);
    if (parsedTarget && parsedTarget > 5000) {
      setShowBigWarning(true);
      return;
    }
    runGenerate();
  }, [chosenReplaceMode, parsedTarget, runGenerate]);

  // Cancel generation
  const handleCancelGeneration = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setIsGenerating(false);
    setJobId(null);
    setJobStatus(null);
    setProgress(0);
    toast.info('Generation cancelled');
  }, []);

  // Confirm and insert schedule
  const handleConfirmInsert = useCallback(async () => {
    if (!generatedSchedule?.tasks) return;

    setShowConfirmDialog(false);
    setIsGenerating(true);

    try {
      const response = await api.post(`/ai-schedule/insert/${projectId}`, generatedSchedule.tasks, {
        params: { mode: insertMode }
      });

      if (response.data.success) {
        toast.success(`Inserted ${response.data.inserted_count} tasks into schedule!`);
        onScheduleGenerated?.(response.data.tasks);
        setGeneratedSchedule(null);
        setPrompt('');
        onClose();
      }
    } catch (error) {
      console.error('Insert error:', error);
      toast.error(error.response?.data?.detail || 'Failed to insert schedule');
    } finally {
      setIsGenerating(false);
    }
  }, [generatedSchedule, projectId, insertMode, onScheduleGenerated, onClose]);

  // Apply example prompt
  const applyExample = (example) => {
    setPrompt(example.prompt);
    toast.info(`Applied "${example.title}" template`);
  };

  if (!isOpen) return null;

  const panelContent = (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[9998]"
        onClick={onClose}
      />
      
      {/* Slide-out Panel — force dark scheme so slate-based styling stays readable in light mode too */}
      <div className="dark fixed right-0 top-0 h-full w-[520px] bg-slate-900 border-l border-slate-700 z-[9999] flex flex-col shadow-2xl animate-in slide-in-from-right duration-300 text-slate-100"
           style={{ colorScheme: 'dark' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-gradient-to-r from-slate-900 to-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/30">
              <Sparkles className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">AI Schedule Generator</h2>
              <p className="text-xs text-slate-400">Powered by Cognara</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 p-6">
          <div className="space-y-6">
            {/* Project Info */}
            <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
              <div className="flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="text-slate-400">Project:</span>
                <span className="text-white font-medium">{projectName}</span>
              </div>
            </div>

            {/* Prompt Input */}
            <div className="space-y-3">
              <Label className="text-white flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-yellow-400" />
                Describe Your Schedule
              </Label>
              <Textarea
                placeholder="Example: Create a schedule for construction of a 200 MW solar power plant. Include engineering, procurement, civil works, module installation, electrical installation, testing and commissioning."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onPaste={(e) => {
                  // Explicit paste handler — bypasses any upstream event
                  // interception (dialog/scrollarea/global hotkeys) that might
                  // swallow the browser's default paste.
                  try {
                    const text = e.clipboardData?.getData('text');
                    if (!text) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const el = e.target;
                    const start = el.selectionStart ?? prompt.length;
                    const end   = el.selectionEnd   ?? prompt.length;
                    const next  = prompt.slice(0, start) + text + prompt.slice(end);
                    setPrompt(next);
                    requestAnimationFrame(() => {
                      try { el.setSelectionRange(start + text.length, start + text.length); } catch { /* noop */ }
                    });
                  } catch { /* fall through — allow default */ }
                }}
                spellCheck={false}
                className="min-h-[140px] bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 resize-none"
                data-testid="ai-prompt-input"
              />
              <p className="text-xs text-slate-500">
                Be specific about phases, activities, and any constraints. The more detail you provide, the better the schedule.
              </p>
            </div>

            {/* Example Prompts */}
            <div className="space-y-3">
              <Label className="text-slate-400 text-xs uppercase tracking-wider">Quick Templates</Label>
              <div className="grid grid-cols-2 gap-2">
                {examplePrompts.map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => applyExample(example)}
                    className="p-3 rounded-lg bg-slate-800 border border-slate-700 hover:border-purple-500/50 hover:bg-slate-800/80 transition-all text-left group"
                  >
                    <span className="text-sm text-white group-hover:text-purple-300">{example.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Advanced Options */}
            <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
              <CollapsibleTrigger className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
                <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                Advanced Options
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Project Type */}
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-xs">Project Type</Label>
                    <Select value={projectType} onValueChange={setProjectType}>
                      <SelectTrigger className="bg-slate-800 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 z-[10001]">
                        <SelectItem value="Infrastructure">Infrastructure</SelectItem>
                        <SelectItem value="EPC">EPC</SelectItem>
                        <SelectItem value="IT">IT / Software</SelectItem>
                        <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Project Size — hidden when a Custom Target Task Count is provided */}
                  {!parsedTarget && (
                    <div className="space-y-2">
                      <Label className="text-slate-300 text-xs">Project Size / Detail Level</Label>
                      <Select value={projectSize} onValueChange={setProjectSize}>
                        <SelectTrigger className="bg-slate-800 border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700 z-[10001]">
                          <SelectItem value="Small">Small (30-50 tasks)</SelectItem>
                          <SelectItem value="Medium">Medium (50-80 tasks)</SelectItem>
                          <SelectItem value="Large">Large (80-120 tasks)</SelectItem>
                          <SelectItem value="Mega">Mega / Ultra-Detailed (100-150 tasks)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Custom Target Task Count — up to 50,000. When set, overrides project size. */}
                  <div className={`space-y-2 ${parsedTarget ? 'col-span-2' : 'col-span-2'}`}>
                    <Label className="text-slate-300 text-xs flex items-center gap-2">
                      <Zap className="w-3 h-3 text-purple-400" /> Custom Target Task Count (optional, 1–50,000)
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      max={50000}
                      step={100}
                      value={customTarget}
                      onChange={(e) => setCustomTarget(e.target.value)}
                      placeholder="Leave blank to use the project-size preset above"
                      className="bg-slate-800 border-slate-600 text-white"
                      data-testid="ai-target-task-count"
                    />
                    <p className="text-[11px] text-slate-500">
                      Cognara builds an engineering master plan; a deterministic expander then clones activities
                      with FS predecessors + calendar + resources until the target is met. Above 5,000 you&apos;ll get a soft warning.
                    </p>
                  </div>

                  {/* Planning Level */}
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-xs">Planning Level</Label>
                    <Select value={planningLevel} onValueChange={setPlanningLevel}>
                      <SelectTrigger className="bg-slate-800 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 z-[10001]">
                        <SelectItem value="High-level">High-level</SelectItem>
                        <SelectItem value="Detailed">Detailed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Country */}
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-xs">Country/Region</Label>
                    <Select value={country} onValueChange={setCountry}>
                      <SelectTrigger className="bg-slate-800 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 z-[10001]">
                        <SelectItem value="International">International</SelectItem>
                        <SelectItem value="USA">USA</SelectItem>
                        <SelectItem value="UK">UK</SelectItem>
                        <SelectItem value="India">India</SelectItem>
                        <SelectItem value="UAE">UAE / Middle East</SelectItem>
                        <SelectItem value="Australia">Australia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Start Date */}
                <div className="space-y-2">
                  <Label className="text-slate-300 text-xs flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    Project Start Date
                  </Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-6"
              data-testid="ai-generate-schedule-btn"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating Ultra-Detailed Schedule...
                </>
              ) : (
                <>
                  <Wand2 className="w-5 h-5 mr-2" />
                  Generate Schedule
                </>
              )}
            </Button>

            {/* Generation Progress Info */}
            {isGenerating && (
              <div className="space-y-3 p-4 bg-purple-500/10 rounded-lg border border-purple-500/40 sticky top-2 z-10 shadow-lg shadow-purple-900/30" data-testid="ai-progress-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-400 shrink-0" />
                    <span className="text-sm text-purple-200 font-semibold truncate" data-testid="ai-progress-status">
                      {jobStatus || 'Initializing…'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-2xl font-bold text-purple-300 tabular-nums" data-testid="ai-progress-percent">{progress}%</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelGeneration}
                      className="text-slate-400 hover:text-white h-7 px-2"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
                <Progress value={progress} className="h-2.5" data-testid="ai-progress-bar" />
                <div className="grid grid-cols-4 gap-1 text-[10px]" data-testid="ai-progress-steps">
                  {[
                    { key: 'plan', label: 'Master Plan', threshold: 20 },
                    { key: 'expand', label: 'Expander', threshold: 45 },
                    { key: 'sched', label: 'Scheduling', threshold: 75 },
                    { key: 'store', label: 'Persist', threshold: 95 },
                  ].map((s) => (
                    <div key={s.key}
                      className={`text-center px-1.5 py-1 rounded ${progress >= s.threshold ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-800/60 text-slate-500'}`}
                      data-testid={`ai-progress-step-${s.key}`}>
                      {progress >= s.threshold ? '✓ ' : ''}{s.label}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400">
                  {parsedTarget
                    ? `Building a ${parsedTarget.toLocaleString()}-activity schedule with resource loading, predecessor logic, calendars and milestones. Runs in background — no client timeout.`
                    : `Building your ${projectSize === 'Mega' ? 'ultra-detailed 100-150 task' : projectSize === 'Large' ? 'detailed 80-120 task' : projectSize === 'Medium' ? '50-80 task' : '30-50 task'} schedule. Typically 1-2 minutes for detailed schedules.`}
                </p>
              </div>
            )}

            {/* Generated Schedule Preview */}
            {generatedSchedule && (
              <div className="space-y-4 animate-in fade-in duration-300">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span className="text-white font-medium">Schedule Generated!</span>
                </div>

                {/* Summary */}
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-green-400" />
                      <span className="text-slate-300">Tasks:</span>
                      <span className="text-white font-semibold">{generatedSchedule.task_count}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-green-400" />
                      <span className="text-slate-300">Milestones:</span>
                      <span className="text-white font-semibold">
                        {generatedSchedule.summary?.total_milestones || 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-400" />
                      <span className="text-slate-300">Duration:</span>
                      <span className="text-white font-semibold">
                        {generatedSchedule.summary?.estimated_duration_days || '—'} days
                      </span>
                    </div>
                  </div>
                </div>

                {/* Warnings */}
                {generatedSchedule.warnings?.length > 0 && (
                  <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                      <span className="text-amber-300 font-medium text-sm">AI Recommendations</span>
                    </div>
                    <ul className="space-y-1 text-sm text-slate-300">
                      {generatedSchedule.warnings.map((warning, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-amber-400">•</span>
                          {warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Task Preview */}
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Task Preview (First 10)</Label>
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-700 bg-slate-800/50">
                    {generatedSchedule.tasks?.slice(0, 10).map((task, idx) => (
                      <div
                        key={idx}
                        className={`px-3 py-2 flex items-center gap-2 text-sm ${
                          idx % 2 === 0 ? 'bg-slate-800/30' : ''
                        }`}
                      >
                        <span className="text-[#1E4E8C] font-mono text-xs w-12">{task.wbs_code}</span>
                        <span className={`flex-1 ${task.is_summary ? 'text-white font-medium' : 'text-slate-300'}`}>
                          {task.name}
                        </span>
                        {task.is_milestone && (
                          <Badge variant="outline" className="text-amber-400 border-amber-400/50 text-xs">MS</Badge>
                        )}
                        {task.is_summary && (
                          <Badge variant="outline" className="text-purple-400 border-purple-400/50 text-xs">WBS</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setGeneratedSchedule(null)}
                    className="flex-1 border-slate-600"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate
                  </Button>
                  <Button
                    onClick={() => setShowConfirmDialog(true)}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="ai-insert-btn"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Insert into Schedule
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700 bg-slate-800/50">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Zap className="w-3 h-3" />
            <span>AI-powered schedule generation powered by Cognara</span>
          </div>
        </div>
      </div>

      {/* Confirm Insert Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Insert AI-Generated Schedule
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Choose how to add the {generatedSchedule?.task_count || 0} generated tasks to your project.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-600 hover:border-purple-500/50 cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="insertMode"
                  value="replace"
                  checked={insertMode === 'replace'}
                  onChange={() => setInsertMode('replace')}
                  className="text-purple-500"
                />
                <div>
                  <span className="text-white font-medium">Replace existing tasks</span>
                  <p className="text-xs text-slate-400">Clear all current tasks and insert the AI-generated schedule</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-600 hover:border-purple-500/50 cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="insertMode"
                  value="append"
                  checked={insertMode === 'append'}
                  onChange={() => setInsertMode('append')}
                  className="text-purple-500"
                />
                <div>
                  <span className="text-white font-medium">Append to existing tasks</span>
                  <p className="text-xs text-slate-400">Add AI-generated tasks after your current schedule</p>
                </div>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmInsert}
              disabled={isGenerating}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Confirm & Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-generation: existing macro schedule prompt */}
      <Dialog open={showExistingSchedulePrompt} onOpenChange={setShowExistingSchedulePrompt}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="existing-schedule-prompt">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              A macro schedule already exists
            </DialogTitle>
            <DialogDescription className="text-slate-300">
              This project already has <b>{existingTaskCount.toLocaleString()}</b> tasks. Choose what to do with the AI-generated schedule.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-600 hover:border-red-500/50 cursor-pointer transition-colors">
              <input
                type="radio"
                name="preInsertMode"
                value="replace"
                checked={chosenReplaceMode === 'replace'}
                onChange={() => setChosenReplaceMode('replace')}
                className="mt-1 text-red-500"
                data-testid="pre-mode-replace"
              />
              <div>
                <span className="text-white font-medium">Replace existing schedule</span>
                <p className="text-xs text-slate-400">All {existingTaskCount.toLocaleString()} current tasks will be permanently deleted and replaced by the new AI schedule.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-600 hover:border-purple-500/50 cursor-pointer transition-colors">
              <input
                type="radio"
                name="preInsertMode"
                value="append"
                checked={chosenReplaceMode === 'append'}
                onChange={() => setChosenReplaceMode('append')}
                className="mt-1 text-purple-500"
                data-testid="pre-mode-append"
              />
              <div>
                <span className="text-white font-medium">Append to existing schedule</span>
                <p className="text-xs text-slate-400">Keep the current {existingTaskCount.toLocaleString()} tasks and add the new AI-generated tasks after them.</p>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExistingSchedulePrompt(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button
              onClick={proceedAfterExistingPrompt}
              className={chosenReplaceMode === 'replace' ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'}
              data-testid="pre-mode-continue"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {chosenReplaceMode === 'replace' ? 'Replace & Generate' : 'Append & Generate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Large-target soft warning (>5,000 activities) */}
      <Dialog open={showBigWarning} onOpenChange={setShowBigWarning}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              You&apos;re about to generate a very large schedule
            </DialogTitle>
            <DialogDescription className="text-slate-300 space-y-2">
              <span className="block">
                You&apos;ve requested <b>{(parsedTarget || 0).toLocaleString()}</b> activities.
                Anything over ~5,000 is unusual — even mega-EPC projects are typically 5,000–20,000.
              </span>
              <span className="block text-slate-400 text-xs mt-2">
                • Cognara will build the engineering plan (~200 real activities).<br />
                • A deterministic expander will clone leaf work-packages with FS predecessors,
                  calendars and resource loading until we hit your target.<br />
                • The Gantt / grid may become slower to scroll around this size.<br />
                • There is <b>no client-side timeout</b>; the job runs in the background.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBigWarning(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button
              onClick={() => { setShowBigWarning(false); runGenerate(); }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="ai-big-target-confirm"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Continue &amp; Generate {(parsedTarget || 0).toLocaleString()} activities
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // Use portal to render at document body level
  return ReactDOM.createPortal(panelContent, document.body);
};

export default AISchedulePanel;
